import React, { useState, useEffect } from 'react';
import { Box, Column, Grid } from 'lmc-ui-library';
import { Button, Text, Heading, Modal } from 'lmc-ui-library-rebrand';

import { Layouts, PageLayoutParams } from 'layouts/LayoutProvider';
import { PageComponentProps } from 'shared/interface/globalInterface';

import { FormInputWrapper, TextInput } from 'form/infrastructure/reactHookForm';
import { ButtonsContainer, FormWrapper } from './SetPassword.styled';
import Template from '../../components/Template/Template';
import { FormProvider, UseFormReturn, useForm } from 'react-hook-form';
import { FIELD, FIELD_LABEL, Form } from './types';
import useYupValidationResolver from 'form/yup/reactHookFormResolver';
import { validationSchema } from './validationSchema';
import { FormState } from 'react-hook-form';
import Success from '../components/Success';
import ExpiredToken from '../components/ExpiredToken';
import { verifyToken, setPassword, InvalidTokenError, ExpiredTokenError } from '../../api';
import { useHistory } from 'react-router-dom';
import { login } from 'routes/routePaths';

import styles from './SetPassword.module.scss';

interface Props extends PageComponentProps {}

const SetPassword: React.FC<Props> & PageLayoutParams = ({ match }) => {
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isTokenExpired, setIsTokenExpired] = useState<boolean>(false);
  const { token } = match.params;
  const history = useHistory();
  const methods: UseFormReturn<Form> & { formState: FormState<Form> & { errors: any } } = useForm<Form>({
    mode: 'onSubmit',
    resolver: useYupValidationResolver(validationSchema()),
    defaultValues: {
      [FIELD.PASSWORD]: null,
      [FIELD.CONFIRM_PASSWORD]: null,
    },
  });

  const { handleSubmit } = methods;

  const onSubmit = handleSubmit(async (form: Form) => {
    try {
      await setPassword(token, form.password as string);
      await setSubmitted(true);
    } catch (error) {
      await setError(error.response.data.details);
      await setShowModal(true);
    }
  });

  const checkToken = async (token: string) => {
    setIsLoading(true);
    try {
      await verifyToken(token);
    } catch (error) {
      if (error instanceof InvalidTokenError) {
        history.push(login, {
          error: 'You might have used an incorrect or out of date url. Please try again or sign in below.',
        });
      }

      if (error instanceof ExpiredTokenError) {
        setIsTokenExpired(true);
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    checkToken(token);
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <FormWrapper>
        <Template>
          <>
            {!isLoading && (
              <Box>
                <Box.Content className={styles.setPasswordBox}>
                  {!isTokenExpired ? (
                    !submitted ? (
                      <FormProvider<Form> {...methods}>
                        <Grid cols={12}>
                          <Column lg={7} md={10} sm={12} xs={12}>
                            <form onSubmit={onSubmit}>
                              <Heading weight="bold" mode="title">
                                Set password
                              </Heading>

                              <Text weight="light">Please choose a secure password with:</Text>
                              <Text weight="light"> - At least 12 characters</Text>
                              <Text weight="light">
                                {' '}
                                - At least 1 number, 1 uppercase, 1 lowercase and 1 special character{' '}
                              </Text>
                              <Text weight="light"> - Do not use common words or names</Text>

                              <FormInputWrapper
                                newUi
                                title={FIELD_LABEL.PASSWORD}
                                name={FIELD.PASSWORD}
                                showMultipleErrors={true}>
                                <TextInput newUi type="password" />
                              </FormInputWrapper>

                              <FormInputWrapper
                                newUi
                                title={FIELD_LABEL.CONFIRM_PASSWORD}
                                name={FIELD.CONFIRM_PASSWORD}>
                                <TextInput newUi type="password" />
                              </FormInputWrapper>

                              <ButtonsContainer>
                                <Button borderVariant="rectangle" mode="primary" mt={4} mb={5}>
                                  Set password
                                </Button>
                              </ButtonsContainer>
                            </form>
                          </Column>
                        </Grid>
                      </FormProvider>
                    ) : (
                      <Success />
                    )
                  ) : (
                    <>{isTokenExpired && <ExpiredToken />}</>
                  )}
                </Box.Content>
              </Box>
            )}
          </>
        </Template>
      </FormWrapper>
      {showModal && error ? (
        <Modal showCloseBtn={true} isOpen={showModal} onClose={() => setShowModal(false)}>
          <Modal.Header title="Something went wrong!" mode="error" />
          <Modal.Content>
            {error.map((err: any) => (
              <p>{err}</p>
            ))}
          </Modal.Content>
        </Modal>
      ) : (
        undefined
      )}
    </>
  );
};

SetPassword.layout = Layouts.REGISTRATION;

export default SetPassword;
